import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { HelpArticle } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteHelpArticle } from '../hooks/use-help-article-mutations';

export interface DeleteHelpArticleDialogProps {
  storeId: string;
  row: HelpArticle | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/** The article and its votes go; the backend writes no redirect for a deleted article. */
export function DeleteHelpArticleDialog({
  storeId,
  row,
  onOpenChange,
  onDeleted,
}: DeleteHelpArticleDialogProps) {
  const { t } = useTranslation(['helpCenter', 'common', 'errors']);
  const remove = useDeleteHelpArticle(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('helpCenter:deleteArticle.title', { title: row?.title ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('helpCenter:deleteArticle.description', { handle: row?.handle ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (row) {
                remove.mutate(row.id, {
                  onSuccess: () => {
                    onOpenChange(false);
                    onDeleted?.();
                  },
                });
              }
            }}
          >
            {remove.isPending
              ? t('helpCenter:deleteArticle.submitting')
              : t('helpCenter:deleteArticle.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
